import { supabase } from 'common/src/supabase/index';

export type SettlementDocumentSourceFlow = 'fruitmarkets' | 'multi_shipment';

export interface UploadedSettlementDocument {
  id: string;
  bucketId: 'settlements_documents';
  storagePath: string;
  storedFileName: string;
}

const MAX_ORIGINAL_DOCUMENT_SIZE = 25 * 1024 * 1024;
const ALLOWED_EXTENSIONS = new Set(['pdf', 'xls', 'xlsx']);

const MIME_TYPES_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

const getExtension = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  if (!ALLOWED_EXTENSIONS.has(extension)) {
    throw new Error(
      'El documento original debe ser un archivo PDF, XLS o XLSX.'
    );
  }
  return extension;
};

const sanitizeBaseName = (fileName: string, extension: string) => {
  const suffix = `.${extension}`;
  const baseName = fileName.toLowerCase().endsWith(suffix)
    ? fileName.slice(0, -suffix.length)
    : fileName;
  const sanitized = baseName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/^LIQUIDACION_ORIGINAL_/i, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 120);

  return sanitized || 'DOCUMENTO';
};

const getCanonicalMimeType = (file: File, extension: string) =>
  MIME_TYPES_BY_EXTENSION[extension] || file.type || 'application/octet-stream';

const calculateSha256 = async (file: File) => {
  const hash = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(hash))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

export const markSettlementDocumentFailed = async (documentId: string) => {
  const { error } = await supabase
    .from('source_documents')
    .update({ status: 'failed' })
    .eq('id', documentId)
    .neq('status', 'applied');

  if (error) {
    console.error(
      'Error marking original settlement document as failed:',
      error
    );
  }
};

export const uploadOriginalSettlementDocument = async ({
  file,
  exporterId,
  exporterReceiverId,
  userId,
  sourceFlow,
}: {
  file: File;
  exporterId: number;
  exporterReceiverId?: number | null;
  userId: number;
  sourceFlow: SettlementDocumentSourceFlow;
}): Promise<UploadedSettlementDocument> => {
  if (file.size <= 0 || file.size > MAX_ORIGINAL_DOCUMENT_SIZE) {
    throw new Error('El documento original debe pesar como máximo 25MB.');
  }

  const extension = getExtension(file.name);
  const mimeType = getCanonicalMimeType(file, extension);
  const documentId = crypto.randomUUID();
  const storedFileName = `LIQUIDACION_ORIGINAL_${sanitizeBaseName(
    file.name,
    extension
  )}.${extension}`;
  const storagePath = `${exporterId}/originals/${documentId}/${storedFileName}`;
  const sha256 = await calculateSha256(file);

  const { error: metadataError } = await supabase
    .from('source_documents')
    .insert({
      id: documentId,
      exporter_id: exporterId,
      exporter_receiver_id: exporterReceiverId || null,
      file_name: file.name,
      storage_path: storagePath,
      bucket_id: 'settlements_documents',
      document_type: 'original_settlement',
      source_flow: sourceFlow,
      mime_type: mimeType,
      file_size: file.size,
      file_extension: extension,
      sha256,
      status: 'reviewing',
      created_by: userId,
    });

  if (metadataError) throw metadataError;

  const { error: uploadError } = await supabase.storage
    .from('settlements_documents')
    .upload(storagePath, file, {
      contentType: mimeType,
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    await markSettlementDocumentFailed(documentId);
    throw uploadError;
  }

  return {
    id: documentId,
    bucketId: 'settlements_documents',
    storagePath,
    storedFileName,
  };
};
